type Obj =
  | { type: 'circle'; tMs: number; x: number; y: number }
  | {
      type: 'slider';
      tMs: number;
      /** Alias for start time used by downstream utilities */
      tStart?: number;
      durMs: number;
      /** Explicit end time in milliseconds */
      tEnd: number;
      /** Duration in milliseconds */
      duration: number;
      pts?: { x: number; y: number }[];
      path?: string[];
      points?: { x: number; y: number }[];
      repeats?: number;
    }
  | { type: 'spinner'; tMs: number; durMs: number; x: number; y: number };
import {
  MIN_NODE_DIST,
  MIN_SLIDER_DIST,
  pickPosFlow,
  mergeConsecutiveSliders,
} from './generateHelpers.ts';
import {
  pointsToCommands,
  assertNoConsecutiveSliders,
  assertNoDegenerateSliders,
} from '../utils/coalesceSliderChains';
import { commandsToPath, tessellateCommands } from '../utils/pathLinker';

// forbid new object within 0ms after slider end
export const POST_GAP_MS = 0;

type ActiveNode = { x: number; y: number; tEnd: number };

function directedChart(
  odf: Float32Array,
  hopSec: number,
  beatsMs: number[],
  rmsBeat: number[],
  centroidBeat: number[],
  canvasW: number,
  canvasH: number,
  seed = 1,
): Obj[] {
  const rand = mulberry32(seed);
  const objs: Obj[] = [];
  const eps = 1e-6;

  // --- constants (tune) ---
  const MIN_SLIDER_MS = 240; // floor to avoid stubs
  const MAX_SLIDER_MS = 1800; // cap
  const ROLL_MAX_MS = 600;
  const SPINNER_MIN_MS = 1500; // real spinner only on long gaps
  const SPINNER_GAP_B = 2.0; // ≥2 beats gap → spinner candidate
  const NO_OVERLAP_GUARD_MS = 24; // tiny guard

  // --- active geometry and last slider handle ---
  const active: ActiveNode[] = [];
  let lastSliderIdx: number | null = null;

  // helper signals per beat
  const B: number[] = [];
  const S: number[] = [];
  const D: number[] = [];
  const C: number[] = centroidBeat.slice();
  const beatDurMs: number[] = [];

  const PATH_JOIN_TOL = 10; // px distance between A.end and B.start
  const MAX_CHAIN_GAP_FRAC = 0.25; // ≤ 1/4 beat gap is still “chain”
  const medianBeat = () => {
    const a = beatDurMs.filter(Number.isFinite).slice().sort((x, y) => x - y);
    return a.length ? a[Math.floor(a.length / 2)] : 500;
  };

  // time→frame
  const fFromMs = (ms: number) =>
    Math.max(0, Math.min(odf.length - 1, Math.round((ms / 1000) / hopSec)));

  // per-beat features
  for (let i = 0; i < beatsMs.length; i++) {
    const t0 = beatsMs[i];
    const t1 = beatsMs[i + 1] ?? t0 + (60 * 1000) / 120;
    const f0 = fFromMs(t0),
      f1 = fFromMs(t1);
    beatDurMs[i] = Math.max(1, t1 - t0);

    // beat salience
    const bi = f0;
    const b = Math.max(0, Math.max(odf[bi - 1] || 0, odf[bi] || 0, odf[bi + 1] || 0));
    const loc =
      slidingMax(odf, Math.max(0, bi - 4), Math.min(odf.length - 1, bi + 4)) || 1;
    B[i] = b / (loc || 1);

    // sustain index: RMS vs ODF energy
    let eO = 0;
    for (let f = f0; f < f1; f++) eO += odf[f] || 0;
    const r = rmsBeat[i] || 0;
    S[i] = r / (eO + eps);

    // onset density (peak count)
    let n = 0;
    for (let f = Math.max(0, f0 - 1); f < Math.min(odf.length - 1, f1 + 1); f++) {
      if (
        odf[f] > 0 &&
        odf[f] >= (odf[f - 1] || 0) &&
        odf[f] >= (odf[f + 1] || 0)
      )
        n++;
    }
    D[i] = n;
  }

  // local normalize sustain
  const S0 = S.slice();
  for (let i = 0; i < beatsMs.length; i++) {
    const w = range(i - 4, i + 4);
    const m = Math.max(...w.map((j) => S0[j] || 0));
    S[i] = m ? (S[i] || 0) / m : 0;
  }

  // classify
  const Z: ('burst' | 'groove' | 'sustain' | 'transition')[] = [];
  for (let i = 0; i < beatsMs.length; i++) {
    const w = range(i - 2, i + 1);
    const d = mean(w.map((j) => D[j] || 0));
    const s = mean(w.map((j) => S[j] || 0));
    const b = mean(w.map((j) => B[j] || 0));
    const varB = variance(w.map((j) => B[j] || 0));
    if (d >= 2 || varB >= 0.12) Z[i] = 'burst';
    else if (s >= 0.7 && d <= 0.7) Z[i] = 'sustain';
    else if (b < 0.25 && s <= 0.3) Z[i] = 'transition';
    else Z[i] = 'groove';
  }

  // placement helpers
  let lastPos = { x: canvasW * 0.5, y: canvasH * 0.6 };
  let lastAngle: number | null = null;
  const jump = (mag: number, minDist: number = MIN_NODE_DIST) =>
    pickPosFlow(lastPos, lastAngle, rand, canvasW, canvasH, mag, active, minDist);
  const tailSafe = (p: { x: number; y: number }) => {
    if (lastSliderIdx == null) return true;
    const last = objs[lastSliderIdx] as Extract<Obj, { type: 'slider' }>;
    const end = last.pts[last.pts.length - 1];
    const dx = p.x - end.x,
      dy = p.y - end.y;
    return dx * dx + dy * dy >= MIN_SLIDER_DIST * MIN_SLIDER_DIST;
  };

  // main loop
  for (let i = 0; i < beatsMs.length; i++) {
    const t0 = beatsMs[i];
    const t1 = beatsMs[i + 1] ?? t0 + 500;
    const dur = t1 - t0;

    // retire inactive nodes
    for (let j = active.length - 1; j >= 0; j--)
      if (active[j].tEnd <= t0) active.splice(j, 1);

    // if a slider is still active at this beat, extend or add repeat instead of overlap
    if (extendOrRepeatActiveSliderIfNeeded(t0)) continue;

    // spinner trigger: long gap or quiet sustained plateau
    if (shouldMakeSpinner(i)) {
      const x = canvasW / 2,
        y = canvasH / 2;
      const durMs = clamp(
        Math.min(4000, beatsGapMs(i)),
        SPINNER_MIN_MS,
        4000,
      );
      objs.push({ type: 'spinner', tMs: t0, durMs, x, y });
      active.push({ x, y, tEnd: t0 + durMs });
      lastSliderIdx = null;
      continue;
    }

    if (lastSliderIdx != null) {
      const last = objs[lastSliderIdx] as Extract<Obj, { type: 'slider' }>;
      const timeTouch =
        Math.abs(last.tMs + last.durMs - t0) <=
        Math.max(NO_OVERLAP_GUARD_MS, 0.25 * beatDurMs[beatIndexAt(t0)]);
      const startNearTail =
        dist2(last.pts[last.pts.length - 1], lastPos) <= 100; // 10px^2
      if (timeTouch && startNearTail) continue;
    }

    let pCircle = jump(B[i] >= 0.7 ? 180 : 110);
    if (!tailSafe(pCircle)) pCircle = jump(200, MIN_SLIDER_DIST * 1.25);

    // long slider
    if (S[i] >= 0.8 && B[i] <= 0.6 && D[i] <= 1 && dur >= 0.75 * beatDurMs[i]) {
      const p0 = lastPos;
      const p1 = jump(120, MIN_SLIDER_DIST);
      const lenRaw = dur * (S[i] > 0.9 ? 2 : 1);
      const len = clamp(
        lenRaw,
        Math.max(MIN_SLIDER_MS, 0.8 * beatDurMs[i]),
        MAX_SLIDER_MS,
      );

      const pts = makeArc(p0, p1, C[i], C[i + 1] ?? C[i]);
      const repeats = D[i] >= 1.5 && S[i] < 0.85 ? 1 : 0;

      const tEnd = t0 + len;
      const obj: Obj = {
        type: 'slider',
        tMs: t0,
        durMs: len,
        tEnd,
        duration: len,
        pts,
        repeats,
      };
      objs.push(obj);
      lastSliderIdx = objs.length - 1;

      addActivePath(pts, tEnd);
      lastPos = p1;
      lastAngle = Math.atan2(p1.y - p0.y, p1.x - p0.x);
      continue;
    }

    // roll / short slider
    if (D[i] >= 1.5 && S[i] < 0.7) {
      const p0 = lastPos,
        p1 = jump(90, MIN_SLIDER_DIST);
      const durMs = clamp(
        Math.min(ROLL_MAX_MS, dur),
        Math.max(MIN_SLIDER_MS, 0.5 * beatDurMs[i]),
        ROLL_MAX_MS,
      );
      const pts = [p0, p1];
      const tEnd = t0 + durMs;
      const obj: Obj = {
        type: 'slider',
        tMs: t0,
        durMs,
        tEnd,
        duration: durMs,
        pts,
        repeats: 1,
      };
      objs.push(obj);
      lastSliderIdx = objs.length - 1;

      addActivePath(pts, tEnd);
      lastPos = p1;
      lastAngle = Math.atan2(p1.y - p0.y, p1.x - p0.x);
      continue;
    }

    // default circle
    objs.push({ type: 'circle', tMs: t0, x: pCircle.x, y: pCircle.y });
    active.push({ x: pCircle.x, y: pCircle.y, tEnd: t0 + 500 });
    const prev = lastPos;
    lastPos = pCircle;
    lastAngle = Math.atan2(pCircle.y - prev.y, pCircle.x - prev.x);
    lastSliderIdx = null;
  }

  const result = coalesceSliderRuns(mergeConsecutiveSliders(objs), medianBeat());
  assertNoConsecutiveSliders(result);
  assertNoDegenerateSliders(result);
  return result;

  // ===== helpers =====

  function mulberry32(a: number) {
    return function () {
      let t = (a += 0x6d2b79f5);
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function mean(a: number[]) {
    return a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0;
  }
  function variance(a: number[]) {
    const m = mean(a);
    return a.length ? a.reduce((s, v) => s + (v - m) * (v - m), 0) / a.length : 0;
  }
  function range(a: number, b: number) {
    const out: number[] = [];
    for (let i = Math.max(0, a); i <= Math.min(beatsMs.length - 1, b); i++) out.push(i);
    return out;
  }
  function clamp(v: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, v));
  }
  function slidingMax(x: Float32Array, a: number, b: number) {
    let m = 0;
    for (let i = a; i <= b; i++) m = Math.max(m, x[i] || 0);
    return m;
  }

  function beatIndexAt(tMs: number): number {
    // last beat ≤ tMs
    let lo = 0,
      hi = beatsMs.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (beatsMs[mid] <= tMs + 1e-3) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  // curved control with normalized perpendicular; curvature scales with centroid delta
  function makeArc(
    p0: { x: number; y: number },
    p1: { x: number; y: number },
    c0: number,
    c1: number,
  ) {
    const dx = p1.x - p0.x,
      dy = p1.y - p0.y;
    const L = Math.hypot(dx, dy) || 1;
    const vx = dx / L,
      vy = dy / L; // chord dir
    const nx = -vy,
      ny = vx; // perp
    const bend = Math.sign((c1 - c0) || 0.0001);
    const tang = 0.35 * L; // tangent strength
    const curv = 0.35 * L * Math.min(1, Math.abs(c1 - c0) / 0.3);

    // enforce tangent at start: c = p0 + α*v + β*n
    const cx = p0.x + tang * vx + bend * curv * nx;
    const cy = p0.y + tang * vy + bend * curv * ny;
    return [p0, { x: cx, y: cy }, p1];
  }

  function addActivePath(pts: { x: number; y: number }[], tEnd: number) {
    const pushPt = (x: number, y: number) => active.push({ x, y, tEnd });
    for (let j = 0; j < pts.length - 1; j++) {
      const a = pts[j],
        b = pts[j + 1];
      // 7 samples along each segment ≈ covers ribbon
      for (let u = 0; u <= 1.0001; u += 1 / 6) {
        const x = a.x + u * (b.x - a.x),
          y = a.y + u * (b.y - a.y);
        pushPt(x, y);
      }
    }
  }

  // If a slider is ongoing at t0, extend or add a repeat instead of overlapping
  function extendOrRepeatActiveSliderIfNeeded(t0: number): boolean {
    if (lastSliderIdx == null) return false;
    const last = objs[lastSliderIdx] as Extract<Obj, { type: 'slider' }>;
    const lastEnd = last.tMs + last.durMs;

    // Still inside ribbon → extend/repeat
    if (t0 < lastEnd - NO_OVERLAP_GUARD_MS) {
      if (DTimeWindow(t0, lastEnd) >= 1) last.repeats = (last.repeats ?? 0) + 1;
      else {
        const bIdx = beatIndexAt(last.tMs);
        const baseBeat =
          beatDurMs[bIdx] ??
          (beatsMs[bIdx + 1] ?? last.tMs + 400) - beatsMs[bIdx];
        const add = Math.min(
          MAX_SLIDER_MS - last.durMs,
          Math.max(0.5 * baseBeat, 120),
        );
        last.durMs += Math.max(0, add);
        last.duration = last.durMs;
        last.tEnd = last.tMs + last.durMs;
      }
      return true;
    }

    // Too close after the tail → don’t place anything in the ribbon
    if (t0 - lastEnd < POST_GAP_MS) return true;

    return false;
  }

  function DTimeWindow(tA: number, tB: number): number {
    const fA = fFromMs(tA),
      fB = fFromMs(tB);
    let n = 0;
    for (let f = Math.max(0, fA); f < Math.min(odf.length - 1, fB); f++) {
      if (
        odf[f] > 0 &&
        odf[f] >= (odf[f - 1] || 0) &&
        odf[f] >= (odf[f + 1] || 0)
      )
        n++;
    }
    return n;
  }

  function beatsGapMs(i: number): number {
    const nextBarOrBeat = beatsMs[i + 1] ?? beatsMs[i] + beatDurMs[i];
    return nextBarOrBeat - beatsMs[i];
  }

  function shouldMakeSpinner(i: number): boolean {
    const gap = beatsGapMs(i);
    if (gap >= SPINNER_MIN_MS) return true; // long rest / break
    // quiet sustained plateau over 2 beats: low odf, high rms
    const i1 = Math.min(beatsMs.length - 1, i + 1);
    const eO = energyOdfBetween(
      beatsMs[i],
      beatsMs[i1 + 1] ?? beatsMs[i] + 1000,
    );
    const rAvg = mean([rmsBeat[i] || 0, rmsBeat[i1] || 0]);
    return eO < 0.15 * (rAvg + eps);
  }

  function energyOdfBetween(aMs: number, bMs: number): number {
    const f0 = fFromMs(aMs),
      f1 = fFromMs(bMs);
    let s = 0;
    for (let f = f0; f < f1; f++) s += odf[f] || 0;
    return s / Math.max(1, f1 - f0);
  }

  // --- helpers for chain fitting ---
  function dist2(a: { x: number; y: number }, b: { x: number; y: number }) {
    const dx = a.x - b.x,
      dy = a.y - b.y;
    return dx * dx + dy * dy;
  }
  function lerp(
    a: { x: number; y: number },
    b: { x: number; y: number },
    t: number,
  ) {
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  }
  function quadEval(
    p0: { x: number; y: number },
    c: { x: number; y: number },
    p2: { x: number; y: number },
    t: number,
  ) {
    const u = 1 - t;
    return {
      x: u * u * p0.x + 2 * u * t * c.x + t * t * p2.x,
      y: u * u * p0.y + 2 * u * t * c.y + t * t * p2.y,
    };
  }
  function sampleSlider(
    sl: Extract<Obj, { type: 'slider' }>,
    N = 12,
  ): { x: number; y: number }[] {
    if (Array.isArray(sl.points) && sl.points.length) return sl.points;
    const P = sl.pts || [];
    if (P.length === 3) {
      const out = [] as { x: number; y: number }[];
      for (let i = 0; i <= N; i++) out.push(quadEval(P[0], P[1], P[2], i / N));
      return out;
    } else if (P.length >= 2) {
      const out = [] as { x: number; y: number }[];
      for (let i = 0; i <= N; i++) out.push(lerp(P[0], P[1], i / N));
      return out;
    }
    return [];
  }
  function fitQuadControlLS(
    P0: { x: number; y: number },
    P2: { x: number; y: number },
    S: { x: number; y: number }[],
    T: number[],
  ) {
    // basis for control: B(t)=2(1-t)t
    let numX = 0,
      numY = 0,
      den = 0;
    for (let k = 0; k < S.length; k++) {
      const t = T[k],
        u = 1 - t,
        B = 2 * u * t;
      const zx = S[k].x - (u * u * P0.x + t * t * P2.x);
      const zy = S[k].y - (u * u * P0.y + t * t * P2.y);
      numX += B * zx;
      numY += B * zy;
      den += B * B;
    }
    if (den < 1e-8) return null;
    return { x: numX / den, y: numY / den };
  }
  function chordParams(S: { x: number; y: number }[]) {
    const d = [0];
    let acc = 0;
    for (let i = 1; i < S.length; i++) {
      acc += Math.hypot(S[i].x - S[i - 1].x, S[i].y - S[i - 1].y);
      d.push(acc);
    }
    const T = d.map((v) => (acc > 0 ? v / acc : 0));
    return T;
  }

  // --- robust run coalescer ---
  function coalesceSliderRuns(
    list: Obj[],
    beatMs: number,
    tolPx = PATH_JOIN_TOL,
  ): Obj[] {
    const tolMs = Math.max(NO_OVERLAP_GUARD_MS, MAX_CHAIN_GAP_FRAC * beatMs);
    const tolPx2 = tolPx * tolPx;

    const out: Obj[] = [];
    let i = 0;
    while (i < list.length) {
      const e = list[i];
      if (e.type !== 'slider') {
        out.push(e);
        i++;
        continue;
      }

      // grow run while sliders overlap or touch in time and endpoints are close
      let j = i;
      let endTime = e.tMs + e.durMs;
      while (j + 1 < list.length && list[j + 1].type === 'slider') {
        const A = list[j] as Extract<Obj, { type: 'slider' }>;
        const B = list[j + 1] as Extract<Obj, { type: 'slider' }>;
        const Aend = A.points![A.points!.length - 1];
        const Bstart = B.points![0];

        const timeClose = B.tMs - (A.tMs + A.durMs) <= tolMs;
        const spaceClose = dist2(Aend, Bstart) <= tolPx2;
        if (!(timeClose && spaceClose)) break;

        j++;
        endTime = Math.max(endTime, B.tMs + B.durMs);
      }

      if (j === i) {
        out.push(e);
        i++;
        continue;
      }

      // sample the whole chain and fit one quadratic
      const first = list[i] as Extract<Obj, { type: 'slider' }>;
      const last = list[j] as Extract<Obj, { type: 'slider' }>;
      const P0 = first.points![0];
      const P2 = last.points![last.points!.length - 1];

      // concatenate samples; drop duplicate at segment joints
      const S: { x: number; y: number }[] = [];
      for (let k = i; k <= j; k++) {
        const samp = sampleSlider(list[k] as Extract<Obj, { type: 'slider' }>, 12);
        if (S.length) samp.shift();
        S.push(...samp);
      }
      const T = chordParams(S);
      let C = fitQuadControlLS(P0, P2, S, T);
      if (!C) {
        const mx = (P0.x + P2.x) / 2,
          my = (P0.y + P2.y) / 2;
        const dx = P2.x - P0.x,
          dy = P2.y - P0.y,
          L = Math.hypot(dx, dy) || 1;
        C = { x: mx - 0.25 * dy, y: my + 0.25 * dx };
      }

      const cmds = pointsToCommands([P0, C, P2]);
      const path = commandsToPath(cmds);
      const points = tessellateCommands(cmds);
      const duration = endTime - first.tMs;
      out.push({
        type: 'slider',
        tMs: first.tMs,
        tStart: first.tMs,
        durMs: duration,
        tEnd: endTime,
        duration,
        path,
        points,
        repeats: 0,
      });
      i = j + 1;
    }
    return out;
  }
}

export default directedChart;

